import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { WebRoutes } from '@shared/constants/web-routes.enum';
import { UserRole } from '@features/shared/auth/domain/models/authenticated-user.model';
import { resolveLandingRoute } from '@features/shared/auth/domain/utils/landing-route.util';
import { AuthStore } from '@features/shared/auth/presentation/store/auth.store';

// Both onboarding decisions below re-resolve the profile through `refreshProfile()` (same pattern
// as `hasModuleAccessGuard`) instead of trusting the cached, client-mutable snapshot, and read a
// missing `hasCompletedOnboarding` as NOT completed: failing closed only ever affects the
// ClinicOwner wizard case (the backend always sends `true` for every other role).

// Guards the `/onboarding` route itself: only a ClinicOwner who hasn't finished the 5-step
// checklist may enter. Anyone else (wrong role, or a ClinicOwner who already completed it) is
// bounced to their normal landing route instead.
export const onboardingGuard: CanActivateFn = async () => {
  // Both injects must happen BEFORE the `await` below -- `inject()` requires an active injection
  // context, which only survives the synchronous portion of this function (see
  // `hasModuleAccessGuard` for the same NG0203 constraint).
  const store = inject(AuthStore);
  const router = inject(Router);

  await store.refreshProfile();

  const role = store.user()?.role;
  const hasCompletedOnboarding = store.user()?.hasCompletedOnboarding ?? false;

  if (role === UserRole.ClinicOwner && !hasCompletedOnboarding) return true;

  const fallbackRole = role ?? null;
  const needsClinicSelection = (store.user()?.staffClinics?.length ?? 0) > 1;
  return router.createUrlTree([resolveLandingRoute(fallbackRole, hasCompletedOnboarding, needsClinicSelection, store.user()?.accountRealm)]);
};

// Guards the dashboard shell (and every child route under it): a ClinicOwner who hasn't finished
// onboarding must not be able to bypass the checklist by deep-linking `/dashboard` or any of its
// children -- redirect them back to `/onboarding` instead.
export const onboardingRequiredGuard: CanActivateFn = async () => {
  const store = inject(AuthStore);
  const router = inject(Router);

  await store.refreshProfile();

  const role = store.user()?.role;
  const hasCompletedOnboarding = store.user()?.hasCompletedOnboarding ?? false;

  if (role === UserRole.ClinicOwner && !hasCompletedOnboarding) {
    return router.createUrlTree([WebRoutes.Onboarding]);
  }

  return true;
};
